import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SitePopup = ({ site, onClose, onViewDetails }) => {
  if (!site) return null;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cultural': return 'Building2';
      case 'natural': return 'Trees';
      case 'restaurant': return 'UtensilsCrossed';
      case 'accommodation': return 'Home';
      default: return 'MapPin';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'cultural': return 'text-accent';
      case 'natural': return 'text-success';
      case 'restaurant': return 'text-orange-500';
      case 'accommodation': return 'text-blue-500';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'cultural': return 'Cultural Heritage';
      case 'natural': return 'Natural Heritage';
      case 'restaurant': return 'Restaurant';
      case 'accommodation': return 'Accommodation';
      default: return 'Location';
    }
  };

  const getTypeBgColor = (type) => {
    switch (type) {
      case 'cultural': return 'bg-accent/10';
      case 'natural': return 'bg-success/10';
      case 'restaurant': return 'bg-orange-500/10';
      case 'accommodation': return 'bg-blue-500/10';
      default: return 'bg-muted';
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-lg shadow-modal max-w-md w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${getTypeBgColor(site?.type)}`}>
              <Icon 
                name={getTypeIcon(site?.type)} 
                size={20} 
                className={getTypeColor(site?.type)} 
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg text-foreground">
                {site?.name}
              </h3>
              <p className={`text-sm font-medium ${getTypeColor(site?.type)}`}>
                {getTypeLabel(site?.type)}
              </p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          {/* Image Gallery */}
          {site?.images && site?.images?.length > 0 && (
            <div className="p-4 pb-0">
              <div className="grid grid-cols-2 gap-2">
                {site?.images?.slice(0, 2)?.map((image, index) => (
                  <div key={index} className="aspect-[4/3] rounded-lg overflow-hidden">
                    <Image
                      src={image?.src}
                      alt={image?.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="p-4">
            <p className="text-muted-foreground leading-relaxed mb-4">
              {site?.description}
            </p>

            {/* Site Details */}
            <div className="space-y-3">
              {/* Contact Information for Tourism Offerings */}
              {site?.contact && (
                <div className="flex items-center gap-3">
                  <Icon name="Phone" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Contact:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.contact}</span>
                  </div>
                </div>
              )}

              {site?.email && (
                <div className="flex items-center gap-3">
                  <Icon name="Mail" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Email:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.email}</span>
                  </div>
                </div>
              )}

              {/* Heritage Site Information */}
              {site?.historicalPeriod && (
                <div className="flex items-center gap-3">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Historical Period:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.historicalPeriod}</span>
                  </div>
                </div>
              )}

              {site?.significance && (
                <div className="flex items-center gap-3">
                  <Icon name="Award" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Significance:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.significance}</span>
                  </div>
                </div>
              )}

              {site?.area && (
                <div className="flex items-center gap-3">
                  <Icon name="Map" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Area:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.area}</span>
                  </div>
                </div>
              )}

              {site?.established && (
                <div className="flex items-center gap-3">
                  <Icon name="Flag" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Established:</span>
                    <span className="text-sm text-muted-foreground ml-2">{site?.established}</span>
                  </div>
                </div>
              )}

              {/* Trail Information for Tourism Offerings */}
              {site?.trailId && (
                <div className="flex items-center gap-3">
                  <Icon name="Route" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Trail:</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      Part of thematic trail #{site?.trailId}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium text-foreground">Location:</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    {site?.coordinates?.[0]?.toFixed(4)}, {site?.coordinates?.[1]?.toFixed(4)}
                  </span>
                </div>
              </div>

              {site?.country && (
                <div className="flex items-center gap-3">
                  <Icon name="Globe" size={16} className="text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-foreground">Country:</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {site?.country === 'montenegro' ? 'Montenegro' : 'Serbia'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1"
            >
              Close
            </Button>
            {/* Only show View Details for heritage sites */}
            {(site?.type === 'cultural' || site?.type === 'natural') && (
              <Button 
                variant="default" 
                onClick={() => onViewDetails(site)}
                className="flex-1"
                iconName="ExternalLink"
                iconPosition="right"
              >
                View Details
              </Button>
            )}
            {/* Show different action for tourism offerings */}
            {(site?.type === 'restaurant' || site?.type === 'accommodation') && (
              <Button 
                variant="default" 
                onClick={() => onViewDetails(site)}
                className="flex-1"
                iconName="MapPin"
                iconPosition="right"
              >
                Explore Trail
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitePopup;